import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  GripVertical,
  Settings,
  RotateCcw,
  Filter,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react'
import { useGridPrefs, useSaveGridPrefs, type GridPrefs } from '@/hooks/useGridPrefs'

// ── Types ──────────────────────────────────────────────────────────────────────

export interface DataGridColumn<T> {
  id: string
  header: React.ReactNode
  accessor: (row: T) => React.ReactNode
  sortValue?: (row: T) => string | number | null
  filterValue?: (row: T) => string
  visible?: boolean
  sortable?: boolean
  filterable?: boolean
  minWidth?: string
  align?: 'left' | 'center' | 'right'
  className?: string
  headerClassName?: string
}

export interface DataGridProps<T> {
  gridId: string
  columns: DataGridColumn<T>[]
  data: T[]
  isLoading?: boolean
  emptyMessage?: string
  emptyIcon?: React.ReactNode
  onRowClick?: (row: T) => void
  rowClassName?: (row: T) => string
  pageSize?: number
  /** If provided, clicking a row toggles an inline accordion with this content. */
  renderExpandedRow?: (row: T) => React.ReactNode
  /** Stable key for tracking expanded state. Defaults to row index. */
  getRowKey?: (row: T) => string
}

// ── Component ──────────────────────────────────────────────────────────────────

export function DataGrid<T>({
  gridId,
  columns: rawColumns,
  data,
  isLoading = false,
  emptyMessage = 'Nenhum registro encontrado',
  emptyIcon,
  onRowClick,
  rowClassName,
  pageSize,
  renderExpandedRow,
  getRowKey,
}: DataGridProps<T>) {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }
  // ── Preferences ────────────────────────────────────────────────────────────
  const { data: savedPrefs, isSuccess, isError } = useGridPrefs(gridId)
  const prefsLoaded = isSuccess || isError  // Fallback to defaults if prefs fail
  const { mutate: savePrefs } = useSaveGridPrefs()
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({})
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const [sort, setSort] = useState<{ column: string; direction: 'asc' | 'desc' } | null>(null)

  // Filtros de coluna e página persistidos em sessionStorage (por gridId) —
  // sobrevivem navegação dentro da sessão (ex: abrir registro e voltar) e
  // resetam no próximo browser session.
  const SS_KEY = `dg:${gridId}:state`
  const [filters, setFilters] = useState<Record<string, string>>(() => {
    try { return JSON.parse(sessionStorage.getItem(SS_KEY) || '{}').filters || {} } catch { return {} }
  })
  const [page, setPage] = useState<number>(() => {
    try { return JSON.parse(sessionStorage.getItem(SS_KEY) || '{}').page || 1 } catch { return 1 }
  })
  useEffect(() => {
    try {
      const cur = JSON.parse(sessionStorage.getItem(SS_KEY) || '{}')
      sessionStorage.setItem(SS_KEY, JSON.stringify({ ...cur, filters, page }))
    } catch {}
  }, [SS_KEY, filters, page])

  const [showVisibility, setShowVisibility] = useState(false)
  const visRef = useRef<HTMLDivElement>(null)
  // Flag: true enquanto está fazendo resize de coluna. Quando true, qualquer
  // dragstart é cancelado em capture-phase (vide useEffect abaixo) — assim o
  // HTML5 drag-and-drop do <th draggable> não rouba o evento.
  const isResizingRef = useRef(false)
  useEffect(() => {
    const blockDrag = (e: DragEvent) => {
      if (isResizingRef.current) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
    // Capture-phase: roda ANTES do handler React do <th>, e ANTES do browser
    // realmente iniciar o drag visual.
    document.addEventListener('dragstart', blockDrag, true)
    return () => document.removeEventListener('dragstart', blockDrag, true)
  }, [])

  // Drag state
  const [dragCol, setDragCol] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState<string | null>(null)

  // Initialize from saved prefs or defaults
  useEffect(() => {
    if (!prefsLoaded) return
    const defaultOrder = rawColumns.map((c) => c.id)
    const defaultVis: Record<string, boolean> = {}
    rawColumns.forEach((c) => { defaultVis[c.id] = c.visible !== false })

    // Always set defaults, then overlay saved prefs if available
    setColumnOrder(savedPrefs?.columnOrder?.length ? savedPrefs.columnOrder : defaultOrder)
    setColumnVisibility(savedPrefs?.columnVisibility ? { ...defaultVis, ...savedPrefs.columnVisibility } : defaultVis)
    if (savedPrefs?.sort) setSort(savedPrefs.sort)
    if (savedPrefs?.columnWidths) setColumnWidths(savedPrefs.columnWidths)
    // Ensure columnOrder is never empty (fixes grid not rendering after prefs error)
    if (!savedPrefs?.columnOrder?.length) setColumnOrder(defaultOrder)
  }, [prefsLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced save
  const debouncedSave = useCallback(
    (prefs: GridPrefs) => {
      if (saveTimer.current) clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => {
        savePrefs({ gridId, prefs })
      }, 1000)
    },
    [gridId, savePrefs],
  )

  // Persist on change
  useEffect(() => {
    if (!prefsLoaded || columnOrder.length === 0) return
    debouncedSave({ columnOrder, columnVisibility, sort: sort ?? undefined, columnWidths })
  }, [columnOrder, columnVisibility, sort, columnWidths]) // eslint-disable-line react-hooks/exhaustive-deps

  // Close visibility dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (visRef.current && !visRef.current.contains(e.target as Node)) setShowVisibility(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // ── Ordered & visible columns ──────────────────────────────────────────────
  const columns = useMemo(() => {
    const colMap = new Map(rawColumns.map((c) => [c.id, c]))
    const ordered = columnOrder.length
      ? columnOrder.filter((id) => colMap.has(id)).map((id) => colMap.get(id)!)
      : rawColumns
    // Append any new columns not yet in saved order
    let hasNew = false
    rawColumns.forEach((c) => {
      if (!ordered.find((o) => o.id === c.id)) {
        ordered.push(c)
        hasNew = true
      }
    })
    // Sync columnOrder state if new columns were appended (enables drag for them)
    if (hasNew) {
      const newOrder = ordered.map((c) => c.id)
      if (JSON.stringify(newOrder) !== JSON.stringify(columnOrder)) {
        setColumnOrder(newOrder)
      }
    }
    return ordered
  }, [rawColumns, columnOrder])

  const visibleColumns = useMemo(
    () => columns.filter((c) => columnVisibility[c.id] !== false),
    [columns, columnVisibility],
  )

  // ── Filtering ──────────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let result = data
    for (const col of visibleColumns) {
      const filterText = filters[col.id]
      if (!filterText) continue
      const lower = filterText.toLowerCase()
      result = result.filter((row) => {
        const val = col.filterValue ? col.filterValue(row) : String(col.accessor(row) ?? '')
        return val.toLowerCase().includes(lower)
      })
    }
    return result
  }, [data, filters, visibleColumns])

  // ── Sorting ────────────────────────────────────────────────────────────────
  const sortedData = useMemo(() => {
    if (!sort) return filteredData
    const col = columns.find((c) => c.id === sort.column)
    if (!col || !col.sortValue) return filteredData
    const dir = sort.direction === 'asc' ? 1 : -1
    return [...filteredData].sort((a, b) => {
      const va = col.sortValue!(a)
      const vb = col.sortValue!(b)
      if (va == null && vb == null) return 0
      if (va == null) return 1
      if (vb == null) return -1
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
      return String(va).localeCompare(String(vb)) * dir
    })
  }, [filteredData, sort, columns])

  // ── Pagination ─────────────────────────────────────────────────────────────
  const totalPages = pageSize ? Math.max(1, Math.ceil(sortedData.length / pageSize)) : 1
  const pagedData = pageSize ? sortedData.slice((page - 1) * pageSize, page * pageSize) : sortedData

  // Reset page when data / filters change
  useEffect(() => { setPage(1) }, [filters, data.length])

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSort = (colId: string) => {
    const col = columns.find((c) => c.id === colId)
    if (!col || col.sortable === false || !col.sortValue) return
    setSort((prev) =>
      prev?.column === colId
        ? prev.direction === 'asc'
          ? { column: colId, direction: 'desc' }
          : null
        : { column: colId, direction: 'asc' },
    )
  }

  const handleFilter = (colId: string, value: string) => {
    setFilters((prev) => ({ ...prev, [colId]: value }))
  }

  const toggleVisibility = (colId: string) => {
    setColumnVisibility((prev) => ({ ...prev, [colId]: !prev[colId] }))
  }

  const resetPrefs = () => {
    const order = rawColumns.map((c) => c.id)
    const vis: Record<string, boolean> = {}
    rawColumns.forEach((c) => { vis[c.id] = c.visible !== false })
    setColumnOrder(order)
    setColumnVisibility(vis)
    setSort(null)
    setFilters({})
  }

  // ── Drag & Drop ────────────────────────────────────────────────────────────
  const onDragStart = (colId: string) => setDragCol(colId)
  const onDragEnter = (colId: string) => { if (dragCol && dragCol !== colId) setDragOver(colId) }
  const onDragEnd = () => { setDragCol(null); setDragOver(null) }
  const onDrop = (targetId: string) => {
    if (!dragCol || dragCol === targetId) return
    setColumnOrder((prev) => {
      const order = prev.length ? [...prev] : rawColumns.map((c) => c.id)
      const fromIdx = order.indexOf(dragCol)
      const toIdx = order.indexOf(targetId)
      if (fromIdx === -1 || toIdx === -1) return prev
      order.splice(fromIdx, 1)
      order.splice(toIdx, 0, dragCol)
      return order
    })
    setDragCol(null)
    setDragOver(null)
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  const alignClass = (a?: string) =>
    a === 'center' ? 'text-center' : a === 'right' ? 'text-right' : 'text-left'

  if (!prefsLoaded) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-gray-400">
        Carregando...
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-2 text-xs">
        {/* Column visibility */}
        <div className="relative" ref={visRef}>
          <button
            onClick={() => setShowVisibility((v) => !v)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
          >
            <Settings className="w-3.5 h-3.5" />
            Colunas
          </button>
          {showVisibility && (
            <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 min-w-44 max-h-64 overflow-y-auto">
              {columns.map((col) => (
                <label
                  key={col.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={columnVisibility[col.id] !== false}
                    onChange={() => toggleVisibility(col.id)}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500 w-3.5 h-3.5"
                  />
                  <span className="text-gray-700 dark:text-gray-300 text-xs truncate">{col.header}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Reset */}
        <button
          onClick={resetPrefs}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Resetar
        </button>

        <span className="text-gray-400 dark:text-gray-500 ml-auto">
          {filteredData.length} registro{filteredData.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table wrapper */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm" style={Object.keys(columnWidths).length > 0 ? { tableLayout: 'fixed' as const } : undefined}>
          {/* Header */}
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/80">
              {visibleColumns.map((col) => {
                const isSorted = sort?.column === col.id
                const isSortable = col.sortable !== false && !!col.sortValue
                const isDragTarget = dragOver === col.id

                const savedWidth = columnWidths[col.id]
                const thStyle: React.CSSProperties = { position: 'relative' }
                if (savedWidth) thStyle.width = savedWidth
                if (col.minWidth) thStyle.minWidth = col.minWidth
                if (Object.keys(columnWidths).length > 0) {
                  thStyle.overflow = 'hidden'
                  thStyle.textOverflow = 'ellipsis'
                }

                const handleResizeStart = (e: React.PointerEvent<HTMLDivElement>) => {
                  // Cinto-e-suspensório contra o HTML5 drag-and-drop:
                  //   1) seta flag pra bloqueador document-level cancelar dragstart em capture-phase
                  //   2) remove o atributo draggable do <th> no DOM sincronamente
                  isResizingRef.current = true
                  e.preventDefault()
                  e.stopPropagation()
                  const handle = e.currentTarget
                  const thEl = handle.parentElement as HTMLElement | null
                  if (thEl) thEl.setAttribute('draggable', 'false')
                  const startWidth = thEl?.getBoundingClientRect().width ?? 100
                  const startX = e.clientX
                  try { handle.setPointerCapture(e.pointerId) } catch {}

                  // Primeira interação de resize? Captura larguras atuais de TODAS as
                  // colunas visíveis para que ao trocar pra table-layout: fixed elas
                  // não colapsem ao tamanho mínimo.
                  if (Object.keys(columnWidths).length === 0 && thEl) {
                    const headerRow = thEl.parentElement
                    const initial: Record<string, number> = {}
                    if (headerRow) {
                      Array.from(headerRow.children).forEach((th) => {
                        const id = (th as HTMLElement).getAttribute('data-col-id')
                        if (id) initial[id] = (th as HTMLElement).getBoundingClientRect().width
                      })
                    }
                    if (Object.keys(initial).length > 0) {
                      setColumnWidths(initial)
                    }
                  }
                  const onMove = (m: PointerEvent) => {
                    const delta = m.clientX - startX
                    const w = Math.max(60, Math.round(startWidth + delta))
                    setColumnWidths(prev => ({ ...prev, [col.id]: w }))
                  }
                  const onUp = () => {
                    try { handle.releasePointerCapture(e.pointerId) } catch {}
                    handle.removeEventListener('pointermove', onMove)
                    handle.removeEventListener('pointerup', onUp)
                    handle.removeEventListener('pointercancel', onUp)
                    document.body.style.cursor = ''
                    document.body.style.userSelect = ''
                    // Restaura draggable no DOM (React vai re-aplicar via JSX no próximo render também)
                    if (thEl) thEl.setAttribute('draggable', 'true')
                    setTimeout(() => { isResizingRef.current = false }, 50)
                  }
                  document.body.style.cursor = 'col-resize'
                  document.body.style.userSelect = 'none'
                  handle.addEventListener('pointermove', onMove)
                  handle.addEventListener('pointerup', onUp)
                  handle.addEventListener('pointercancel', onUp)
                }

                return (
                  <th
                    key={col.id}
                    data-col-id={col.id}
                    draggable
                    onDragStart={(e) => {
                      // Se o usuário iniciou um resize, bloqueia o drag-and-drop nativo
                      if (isResizingRef.current) { e.preventDefault(); return }
                      onDragStart(col.id)
                    }}
                    onDragEnter={() => onDragEnter(col.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(col.id)}
                    onDragEnd={onDragEnd}
                    style={thStyle}
                    className={[
                      'px-3 py-2 font-medium text-xs uppercase tracking-wider whitespace-nowrap select-none',
                      alignClass(col.align),
                      isSorted ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400',
                      isSortable ? 'cursor-pointer hover:text-blue-500 dark:hover:text-blue-300' : '',
                      isDragTarget ? 'border-l-2 border-blue-500' : '',
                      dragCol === col.id ? 'opacity-50' : '',
                      col.headerClassName ?? '',
                    ].join(' ')}
                    onClick={() => handleSort(col.id)}
                  >
                    <div className="flex items-center gap-1 group">
                      <GripVertical className="w-3 h-3 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 cursor-grab shrink-0 transition-opacity" />
                      <span className="truncate">{col.header}</span>
                      {isSortable && (
                        <span className="shrink-0 ml-0.5">
                          {isSorted ? (
                            sort.direction === 'asc' ? (
                              <ArrowUp className="w-3.5 h-3.5" />
                            ) : (
                              <ArrowDown className="w-3.5 h-3.5" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" />
                          )}
                        </span>
                      )}
                    </div>
                    {/* Resize handle — barra estreita no canto direito do TH */}
                    <div
                      role="separator"
                      aria-orientation="vertical"
                      title="Arraste para redimensionar"
                      draggable={false}
                      onPointerDown={handleResizeStart}
                      onDragStart={(e) => e.preventDefault()}
                      onClick={(e) => e.stopPropagation()}
                      style={{ touchAction: 'none' }}
                      className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize select-none hover:bg-blue-400/60 active:bg-blue-500 z-10"
                    />
                  </th>
                )
              })}
            </tr>

            {/* Filter row */}
            <tr className="bg-gray-50/50 dark:bg-gray-800/40 border-t border-gray-100 dark:border-gray-700/50">
              {visibleColumns.map((col) => {
                const filterable = col.filterable !== false
                return (
                  <th key={col.id} className="px-2 py-1.5">
                    {filterable ? (
                      <div className="relative">
                        <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                        <input
                          type="text"
                          value={filters[col.id] ?? ''}
                          onChange={(e) => handleFilter(col.id, e.target.value)}
                          placeholder="Filtrar..."
                          className="w-full h-7 pl-6 pr-2 text-xs rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                        />
                      </div>
                    ) : (
                      <div className="h-7" />
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>

          {/* Body */}
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {pagedData.length === 0 && !isLoading ? (
              <tr>
                <td colSpan={visibleColumns.length} className="text-center py-12 text-gray-400 dark:text-gray-500">
                  <div className="flex flex-col items-center gap-2">
                    {emptyIcon}
                    <span className="text-sm">{emptyMessage}</span>
                  </div>
                </td>
              </tr>
            ) : (
              pagedData.map((row, idx) => {
                const rowKey = getRowKey?.(row) ?? String(idx)
                const isExpanded = renderExpandedRow ? expandedKeys.has(rowKey) : false
                const rowClickable = !!(renderExpandedRow || onRowClick)
                const handleRowClick = () => {
                  if (renderExpandedRow) toggleExpanded(rowKey)
                  else onRowClick?.(row)
                }
                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      onClick={handleRowClick}
                      className={[
                        'bg-white dark:bg-gray-900 hover:bg-gray-50/80 dark:hover:bg-gray-800/60 transition-colors',
                        rowClickable ? 'cursor-pointer' : '',
                        isExpanded ? 'bg-blue-50/40 dark:bg-blue-950/20' : '',
                        rowClassName?.(row) ?? '',
                      ].join(' ')}
                    >
                      {visibleColumns.map((col, ci) => (
                        <td
                          key={col.id}
                          className={[
                            'px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap',
                            alignClass(col.align),
                            col.className ?? '',
                          ].join(' ')}
                          style={Object.keys(columnWidths).length > 0
                            ? { overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 }
                            : undefined}
                          title={typeof col.filterValue === 'function' ? col.filterValue(row) : undefined}
                        >
                          {ci === 0 && renderExpandedRow && (
                            <span className={`inline-block mr-2 text-gray-400 transition-transform ${
                              isExpanded ? 'rotate-90' : ''
                            }`} aria-hidden>▸</span>
                          )}
                          {col.accessor(row)}
                        </td>
                      ))}
                    </tr>
                    {isExpanded && renderExpandedRow && (
                      <tr className="bg-blue-50/20 dark:bg-blue-950/10">
                        <td colSpan={visibleColumns.length} className="px-0 py-0 border-t border-blue-200/40 dark:border-blue-900/40">
                          {renderExpandedRow(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })
            )}
          </tbody>
        </table>

        {/* Neon loader line */}
        {isLoading && (
          <div className="h-0.5 w-full overflow-hidden bg-gray-100 dark:bg-gray-800">
            <div
              className="h-full w-1/3 bg-gradient-to-r from-transparent via-blue-500 to-transparent rounded-full shadow-blue-500/60"
              style={{
                animation: 'neon-slide 1.2s ease-in-out infinite',
                boxShadow: '0 0 8px rgba(59,130,246,0.6)',
              }}
            />
          </div>
        )}
      </div>

      {/* Pagination */}
      {pageSize && totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs text-gray-500 dark:text-gray-400">
          <span>
            Pagina {page} de {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pNum: number
              if (totalPages <= 7) {
                pNum = i + 1
              } else if (page <= 4) {
                pNum = i + 1
              } else if (page >= totalPages - 3) {
                pNum = totalPages - 6 + i
              } else {
                pNum = page - 3 + i
              }
              return (
                <button
                  key={pNum}
                  onClick={() => setPage(pNum)}
                  className={[
                    'w-7 h-7 rounded text-xs font-medium transition-colors',
                    pNum === page
                      ? 'bg-blue-500 text-white'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800',
                  ].join(' ')}
                >
                  {pNum}
                </button>
              )
            })}
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Keyframes for neon loader */}
      <style>{`
        @keyframes neon-slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  )
}

export default DataGrid
